import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useShop } from "@/context/ShopContext";

export default function Checkout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { cart, cartSubtotal, format, currency } = useShop();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("PT");
  const [coupon, setCoupon] = useState(location.state?.coupon || "");
  const [loading, setLoading] = useState(false);

  const startStripe = async () => {
    if (!email || !name) { toast.error("Please complete your contact details"); return; }
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        items: cart.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        coupon_code: coupon || undefined,
        customer_email: email,
        customer_name: name,
        shipping_country: country,
        currency,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (cart.length === 0) return <div className="py-24 text-center">Your cart is empty.</div>;

  return (
    <section className="py-12 lg:py-16" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <h1 className="font-serif-bm text-3xl md:text-4xl mb-8">{t("checkout.title")}</h1>
          <div className="card-soft p-6 space-y-4" data-testid="contact-info">
            <h3 className="font-serif-bm text-xl">{t("checkout.contact")}</h3>
            <input className="bm-input" placeholder={t("checkout.email")} value={email} onChange={e=>setEmail(e.target.value)} type="email" data-testid="checkout-email"/>
            <input className="bm-input" placeholder={t("checkout.fullname")} value={name} onChange={e=>setName(e.target.value)} data-testid="checkout-name"/>
          </div>
          <div className="card-soft p-6 mt-5 space-y-4" data-testid="shipping-info">
            <h3 className="font-serif-bm text-xl">{t("checkout.shipping_addr")}</h3>
            <select className="bm-input" value={country} onChange={e=>setCountry(e.target.value)} data-testid="checkout-country">
              {["PT","ES","FR","DE","IT","GB","NL","BE","AT","IE","SE","DK"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="card-soft p-6 mt-5" data-testid="payment-info">
            <h3 className="font-serif-bm text-xl mb-4">Payment</h3>
            <button onClick={startStripe} disabled={loading} className="btn-primary w-full justify-center" data-testid="pay-stripe-btn">
              {loading ? t("checkout.processing") : <><CreditCard size={16} weight="thin"/> {t("checkout.pay_now")} — Card / Apple Pay / Google Pay</>}
            </button>
            <div className="text-center text-xs text-[var(--bm-muted)] my-3">{t("checkout.or")}</div>
            <button onClick={()=>toast.info("PayPal coming soon — please use card payment")} className="btn-secondary w-full justify-center" data-testid="pay-paypal-btn">
              {t("checkout.paypal")}
            </button>
            <p className="text-[10px] tracking-widest uppercase text-center mt-5 text-[var(--bm-muted)] flex items-center justify-center gap-1.5"><ShieldCheck size={12} weight="thin"/> {t("checkout.trust")}</p>
          </div>
        </div>
        <aside className="lg:col-span-2 card-soft p-6 h-fit sticky top-24" data-testid="checkout-summary">
          <h3 className="font-serif-bm text-xl mb-4">Order</h3>
          <div className="space-y-4 max-h-72 overflow-y-auto scrollbar-thin">
            {cart.map(i => (
              <div key={i.product_id} className="flex gap-3">
                <img src={i.image} alt="" className="w-14 h-16 rounded-lg object-cover"/>
                <div className="flex-1 text-sm"><div className="font-serif-bm">{i.name}</div><div className="text-xs text-[var(--bm-muted)]">Qty {i.quantity}</div></div>
                <div className="text-sm">{format(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-1 text-sm border-t border-[var(--bm-border)] pt-4">
            <div className="flex justify-between"><span>{t("cart.subtotal")}</span><span>{format(cartSubtotal)}</span></div>
            <div className="flex justify-between"><span>{t("cart.shipping")}</span><span>{cartSubtotal >= 150 ? "FREE" : format(9.9)}</span></div>
            <div className="flex justify-between text-base font-medium pt-2 border-t border-[var(--bm-border)] mt-2"><span>{t("cart.total")}</span><span>{format(cartSubtotal + (cartSubtotal >= 150 ? 0 : 9.9))}</span></div>
          </div>
        </aside>
      </div>
    </section>
  );
}
